<script lang="ts" setup>
import { computed, ref } from 'vue'
import _ from 'lodash'
import { routing } from '@/Support/Routing/Functions/routing'
import { CheckCircleIcon, StopCircleIcon } from '@heroicons/vue/24/solid'
import Pagination from '@/Layouts/App/Components/Pagination.vue'
import Search from '@/Layouts/App/Components/Search.vue'
import EmptyBox from '@/Components/App/Global/EmptyBox.vue'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { OhVueIcon } from 'oh-vue-icons'
import { PlaceholderReplacements } from '@/Support/Common/Functions/PlaceholdersReplacer'
import { SelectableParams } from '@/Support/Common/Interfaces/SelectableParams'
import {
  SimpleItemListData,
  SimpleItemsListData,
} from '@/Support/Common/Interfaces/SimpleItemData'
import collect from 'collect.js'
import LoadingIcon from '@/Components/App/Global/LoadingIcon.vue'
import { RoutingParams } from '@/Support/Routing/Interfaces/routing'
import MagnifySmIcon from '@/Components/App/Global/Icons/MagnifySmIcon.vue'

interface Props {
  idsSelected: string[] | string | null
  route?: string | null
  searchPlaceholder?: string
  emptyTitle?: string
  emptyIcon?: unknown
  multiple?: boolean
  idLoading?: string | null
  titleItemClass?: string
  additionalParams?: RoutingParams
  showName?: boolean
  showDescription?: boolean
  defaultItemsData?: SimpleItemsListData | null
  hideSearch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  route: null,
  emptyTitle: 'No one where found',
  multiple: false,
  titleItemClass: 'max-w-72',
  additionalParams: {},
  showName: true,
  showDescription: true,
  defaultItemsData: null,
  emptyIcon: MagnifySmIcon,
})

const params = ref<SelectableParams>({
  id: null,
  search: null,
  limit: 5,
  page: 1,
  filter: {
    with_search: null,
  },
  simple_item_list: true,
})

const IDsSelected = computed(() => collect().wrap(props.idsSelected))

const allIdsSelected = ref<string[]>(IDsSelected.value.toArray())

const emit = defineEmits<{
  (e: 'selected', value: string): void
  (e: 'allSelected', value: string[]): void
  (e: 'itemsLoaded', value: SimpleItemsListData): void
}>()

const itemsData = ref<SimpleItemsListData | null>(props.defaultItemsData)
const items = computed(() => itemsData.value?.data)

const loading = ref(false)
const loadItems = async () => {
  if (props.route === null) {
    return
  }
  loading.value = true
  const response = await window.axios.get(
    routing(props.route, {
      ...params.value,
      ...props.additionalParams,
    }),
  )
  const itemsLoaded = response.data as SimpleItemsListData
  itemsData.value = itemsLoaded
  loading.value = false
  emit('itemsLoaded', itemsLoaded)
}
loadItems()
defineExpose({
  loadItems,
})

const changePage = async (page: number) => {
  params.value.page = page
  await loadItems()
}

const searchText = computed(() => params.value.filter.with_search)
const search = _.debounce((name: string | null) => {
  params.value.filter.with_search = name
  loadItems()
}, 400)

const selected = (item: SimpleItemListData) => {
  syncIds(item.id)
  emit('selected', item.id)
  emit('allSelected', allIdsSelected.value)
}

const syncIds = (id: string) => {
  const ids = allIdsSelected.value
  ids.includes(id)
    ? allIdsSelected.value.splice(ids.indexOf(id), 1)
    : allIdsSelected.value.push(id)
}

const emptyTitleReplacements = computed<PlaceholderReplacements>(() => ({
  search: searchText.value || '',
}))
</script>
<template>
  <div>
    <Search
      v-if="itemsData && itemsData.meta.current_page > 0 && !hideSearch"
      :placeholder="searchPlaceholder || 'Search'"
      main-class="text-neutral-600 dark:text-neutral-300"
      input-class="text-neutral-600 placeholder-neutral-500 dark:text-neutral-300"
      class="my-2 rounded-lg"
      @change="search"
    />
    <div v-if="loading" class="py-16">
      <LoadingIcon :loading="loading" big />
    </div>
    <div v-else>
      <ul
        v-if="items?.length"
        role="list"
        class="divide-y divide-neutral-200 dark:divide-neutral-700"
      >
        <li
          v-for="item in items"
          :key="item.id"
          class="group cursor-pointer flex items-center justify-between py-4 hover:bg-neutral-50 dark:hover:bg-neutral-900"
          @click="selected(item)"
        >
          <div class="flex text-left">
            <div class="ml-3">
              <p
                v-tooltip="item.title"
                class="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate w-[18rem]"
                :class="titleItemClass"
              >
                {{ item.title }}
              </p>
              <span
                v-if="item.name && showName"
                class="rounded-xs bg-neutral-50 dark:bg-neutral-700 px-2 py-0.5 text-xxxs font-medium text-neutral-600 dark:text-neutral-400 ring-1 ring-inset ring-neutral-500/10 uppercase"
              >
                {{ item.name }}
              </span>
              <p
                v-if="item.description && showDescription"
                class="text-xs font-light text-neutral-700 dark:text-neutral-300"
              >
                {{ item.description }}
              </p>
            </div>
          </div>

          <span class="flex items-center gap-4">
            <LoadingIcon :loading="idLoading === item.id" />
            <component
              :is="OhVueIcon"
              :name="
                multiple ? 'ri-checkbox-circle-line' : 'ri-radio-button-line'
              "
              :class="{
                'text-neutral-200 dark:text-neutral-700 group-hover:text-neutral-300 dark:group-hover:text-neutral-800':
                  !IDsSelected.contains(item.id),
                'text-primary-400 dark:text-primary-500 group-hover:text-primary-400 dark:group-hover:text-primary-600':
                  IDsSelected.contains(item.id),
              }"
              class="h-8 w-8 transition ease-in-out hover:scale-110 duration-300"
            />
          </span>
        </li>
      </ul>
      <EmptyBox
        v-else
        :title="emptyTitle"
        :title-replacements="searchText ? emptyTitleReplacements : undefined"
        :icon="emptyIcon"
        icon-class="opacity-30"
      />
    </div>

    <Pagination
      v-if="items?.length"
      :data="itemsData?.meta"
      :slim="true"
      :max-pages="5"
      @change="changePage"
    />
  </div>
</template>
